/**
 * Verlaufsspur für den Rücksprung (Jörg 2026-09-29, Regel „Dokument-Navigation" R2:
 * „Wenn ich aus einer Liste in ein Dokument gehe, muss ich immer über einen Link zurück
 * in die Ansicht kommen und zwar genau an der Stelle wo ich vorher war").
 *
 * „Zurück" geht immer im Verlauf zurück (navigate(delta)), nie als neue Seite auf die
 * Liste — nur so trifft man denselben Verlaufseintrag samt Adresse, Scrollposition und
 * Ansichts-Gedächtnis. Damit die Schrittweite stimmt, merkt sich die Shell je
 * Verlaufsindex Pfad und Seitentitel. resolveBack sucht rückwärts den ersten Eintrag,
 * der nicht zum selben Dokument gehört; das überspringt Unteransichten, Modus-Wechsel
 * und doppelte Einträge.
 *
 * Die Spur liegt im sessionStorage (je Browser-Tab) und wird beim Abmelden geleert,
 * weil sie Dokumenttitel enthält.
 */
export type NavType = 'POP' | 'PUSH' | 'REPLACE';
export type PageKind = 'list' | 'document' | 'other';

export interface TrailEntry {
  /** Verlaufsindex des Routers (history.state.idx) */
  idx: number;
  /** location.key */
  key: string;
  path: string;
  search: string;
  /** Seitentitel für „Zurück zu …" */
  label?: string;
  kind?: PageKind;
}

const STORAGE_KEY = 'onvice.navTrail.v1';
const MAX_ENTRIES = 60;

/** Gehört `path` zum Dokument `selfPath` (dieselbe Seite oder eine Unterseite davon)? */
export function isSameDocument(path: string, selfPath: string): boolean {
  const a = path.replace(/\/+$/, '');
  const b = selfPath.replace(/\/+$/, '');
  if (!b) return false;
  return a === b || a.startsWith(`${b}/`);
}

/** Trägt den aktuellen Ort in die Spur ein und liefert die neue Spur. */
export function recordLocation(trail: TrailEntry[], entry: TrailEntry, navType: NavType): TrailEntry[] {
  const same = trail.find((e) => e.idx === entry.idx);
  // Vor/Zurück landet auf einem Eintrag, den die Spur unter anderem Pfad kennt: Die Spur
  // stammt aus einem früheren Seitenaufruf (harter Seitenwechsel) und ist wertlos.
  if (navType === 'POP' && same && same.path !== entry.path) return [{ ...entry }];
  const before = trail.filter((e) => e.idx < entry.idx);
  const after = trail.filter((e) => e.idx > entry.idx);
  // Titel behalten, solange der Eintrag derselbe Ort bleibt (Filterwechsel schreibt ersetzend).
  const keep = same && same.path === entry.path ? { label: same.label, kind: same.kind } : {};
  const next: TrailEntry = { ...keep, ...entry, label: entry.label ?? keep.label, kind: entry.kind ?? keep.kind };
  // PUSH verwirft den Vorwärts-Verlauf, wie der Browser.
  const merged = navType === 'PUSH' ? [...before, next] : [...before, next, ...after];
  return merged.slice(-MAX_ENTRIES);
}

/** Setzt Titel und Art des Eintrags mit diesem Schlüssel. */
export function labelEntry(trail: TrailEntry[], key: string, label: string, kind?: PageKind): TrailEntry[] {
  return trail.map((e) => (e.key === key ? { ...e, label, kind: kind ?? e.kind } : e));
}

/**
 * Sucht ab `currentIdx` rückwärts den ersten Eintrag, der nicht zum Dokument `selfPath`
 * gehört. null = kein verwertbarer Eintrag (Direktaufruf, Lücke in der Spur) → Rückfall.
 */
export function resolveBack(trail: TrailEntry[], currentIdx: number, selfPath: string): { delta: number; target: TrailEntry } | null {
  if (!Number.isInteger(currentIdx) || currentIdx <= 0) return null;
  const byIdx = new Map(trail.map((e) => [e.idx, e]));
  for (let i = currentIdx - 1; i >= 0; i--) {
    const e = byIdx.get(i);
    if (!e) return null;
    if (e.path === '/login' || e.path.startsWith('/login/')) continue;
    if (isSameDocument(e.path, selfPath)) continue;
    return { delta: i - currentIdx, target: e };
  }
  return null;
}

/** Verlaufsindex des Routers; null, wenn er nicht lesbar ist. */
export function currentHistoryIndex(): number | null {
  if (typeof window === 'undefined') return null;
  const st: unknown = window.history.state;
  if (st && typeof st === 'object' && typeof (st as { idx?: unknown }).idx === 'number') return (st as { idx: number }).idx;
  return null;
}

export function loadTrail(): TrailEntry[] {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((e): e is TrailEntry => !!e && typeof e === 'object' && typeof (e as TrailEntry).idx === 'number' && typeof (e as TrailEntry).path === 'string');
  } catch {
    // Speicher nicht verfügbar (privater Modus) → Rücksprung nutzt den Rückfall.
    return [];
  }
}

export function saveTrail(trail: TrailEntry[]): void {
  try { window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(trail)); } catch { /* Speicher voll oder gesperrt: Rücksprung nutzt den Rückfall */ }
}

export function clearTrail(): void {
  try { window.sessionStorage.removeItem(STORAGE_KEY); } catch { /* nichts zu leeren */ }
}
